import { type FC, memo, type ReactNode, useMemo, useState } from 'react'

import { Command, CommandInput } from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import type { PromptTemplate } from './prompt-template.interface'
import { PromptTemplateCommandContent } from './prompt-template-command-content.atom'
import { PromptTemplateCreateFooter } from './prompt-template-create-footer.atom'
import { createPromptTemplateFilter } from './prompt-template-filter.helper'
import { PromptTemplateTrigger } from './prompt-template-trigger.atom'

export interface PromptTemplatePopoverProps {
  templateList: PromptTemplate[]
  onSelect: (templateId: string) => void
  onCreateNew?: () => void
  isDisabled?: boolean
  className?: string
}

/**
 * Searchable popover for selecting prompt templates. Opens upward from the toolbar.
 */
const PromptTemplatePopoverBase: FC<PromptTemplatePopoverProps> = ({
  templateList,
  onSelect,
  onCreateNew,
  isDisabled = false,
  className,
}): ReactNode => {
  const [isOpen, setIsOpen] = useState(false)
  const filterFn = useMemo(() => createPromptTemplateFilter(templateList), [templateList])

  const handleSelect = (templateId: string): void => {
    onSelect(templateId)
    setIsOpen(false)
  }

  const handleCreateNew = (): void => {
    onCreateNew?.()
    setIsOpen(false)
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <PromptTemplateTrigger isDisabled={isDisabled} className={className} />
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="top">Prompt templates</TooltipContent>
      </Tooltip>
      <PopoverContent side="top" align="start" className="chat-input-templates-content w-72 p-0">
        <Command filter={filterFn}>
          <CommandInput
            placeholder="Search templates..."
            className="chat-input-templates-search h-9"
          />
          <PromptTemplateCommandContent templateList={templateList} onSelect={handleSelect} />
          {onCreateNew && <PromptTemplateCreateFooter onClick={handleCreateNew} />}
        </Command>
      </PopoverContent>
    </Popover>
  )
}

export const PromptTemplatePopover = memo(PromptTemplatePopoverBase)

PromptTemplatePopover.displayName = 'PromptTemplatePopover'
